"use client";

import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { createPortal } from "react-dom";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useDebouncedCallback } from "use-debounce";

type Category = { slug: string; name: string; count: number };

export default function CategoryFilter({
  categories,
}: {
  categories: Category[];
}) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace, push, refresh } = useRouter();

  const selected = useMemo(() => {
    const raw = searchParams.get("category") || "";
    return raw ? raw.split(",").filter(Boolean) : [];
  }, [searchParams]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [catSearch, setCatSearch] = useState("");

  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const catSearchRef = useRef<HTMLInputElement>(null);

  // Shared URL builder — single source of truth for param manipulation
  const buildUrl = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(searchParams.toString());
      params.delete("page");
      mutate(params);
      const qs = params.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [searchParams, pathname]
  );

  // Search — debounced, official Next.js pattern
  const handleSearch = useDebouncedCallback((term: string) => {
    const url = buildUrl((p) => term ? p.set("q", term) : p.delete("q"));
    replace(url);
    refresh();
  }, 300);

  // Category toggle
  const toggleCat = useCallback(
    (slug: string) => {
      const next = selected.includes(slug)
        ? selected.filter((s) => s !== slug)
        : [...selected, slug];
      const url = buildUrl((p) =>
        next.length > 0 ? p.set("category", next.join(",")) : p.delete("category")
      );
      push(url);
      refresh();
    },
    [selected, buildUrl, push, refresh]
  );

  const clearCats = useCallback(() => {
    const url = buildUrl((p) => p.delete("category"));
    push(url);
    refresh();
    setCatSearch("");
    setFilterOpen(false);
  }, [buildUrl, push, refresh]);

  // Position dropdown via direct DOM mutation (no state, no re-renders on scroll)
  const positionDropdown = useCallback(() => {
    if (buttonRef.current && panelRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      panelRef.current.style.top = `${rect.bottom + window.scrollY + 6}px`;
      panelRef.current.style.left = `${rect.left + window.scrollX}px`;
    }
  }, []);

  const handleFilterToggle = () => {
    const willOpen = !filterOpen;
    setFilterOpen(willOpen);
    setCatSearch("");
    if (willOpen) {
      // Position on next frame after portal renders
      requestAnimationFrame(() => {
        positionDropdown();
        catSearchRef.current?.focus();
      });
    }
  };

  // Scroll/resize reposition — genuine external subscription
  useEffect(() => {
    if (!filterOpen) return;
    window.addEventListener("scroll", positionDropdown, true);
    window.addEventListener("resize", positionDropdown);
    return () => {
      window.removeEventListener("scroll", positionDropdown, true);
      window.removeEventListener("resize", positionDropdown);
    };
  }, [filterOpen, positionDropdown]);

  // Outside click + Escape — genuine external subscription
  useEffect(() => {
    if (!filterOpen) return;
    function onMouseDown(e: MouseEvent) {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setFilterOpen(false);
        setCatSearch("");
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setFilterOpen(false);
        setCatSearch("");
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [filterOpen]);

  const filteredCats = useMemo(() => {
    if (!catSearch) return categories;
    const q = catSearch.toLowerCase();
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [categories, catSearch]);

  return (
    <div className="flex flex-wrap items-center gap-2 mb-10 sm:mb-14">
      {/* Filter button */}
      <button
        ref={buttonRef}
        onClick={handleFilterToggle}
        className={`inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-medium rounded-full transition-all flex-shrink-0 ${
          filterOpen || selected.length > 0
            ? "bg-violet-800 text-white"
            : "bg-black/[0.03] text-black/40 hover:bg-black/[0.03] hover:text-black"
        }`}
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
        </svg>
        Filter
        {selected.length > 0 && (
          <span className="min-w-[18px] h-[18px] rounded-full bg-white/20 text-xs font-semibold flex items-center justify-center">
            {selected.length}
          </span>
        )}
      </button>

      {selected.length > 0 && (
        <button
          onClick={clearCats}
          className="text-xs text-black/30 hover:text-black transition-colors flex-shrink-0"
        >
          Clear all
        </button>
      )}

      {/* Search — uncontrolled, debounced */}
      <div className="relative w-full max-w-xs">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/30 pointer-events-none"
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
        </svg>
        <input
          type="text"
          placeholder="Search by title..."
          defaultValue={searchParams.get("q") ?? ""}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full pl-10 pr-3 py-2 text-[12px] bg-black/[0.03] rounded-full outline-none focus:ring-1 focus:ring-black/20 placeholder:text-black/25 transition-all"
        />
      </div>

      {/* Filter dropdown — portal */}
      {filterOpen && typeof document !== "undefined" && createPortal(
        <div
          ref={panelRef}
          style={{ position: "absolute", width: 272, zIndex: 9999 }}
          className="bg-white rounded-xl border border-black/[0.06] shadow-[0_12px_40px_-12px_rgba(0,0,0,0.15)] overflow-hidden"
        >
          {categories.length > 6 && (
            <div className="p-2 border-b border-black/[0.04]">
              <input
                ref={catSearchRef}
                type="text"
                placeholder="Find a category..."
                value={catSearch}
                onChange={(e) => setCatSearch(e.target.value)}
                className="w-full px-3 py-1.5 text-[12px] bg-black/[0.02] rounded-lg outline-none focus:ring-1 focus:ring-black/20 placeholder:text-black/25"
              />
            </div>
          )}

          <div className="max-h-64 overflow-y-auto overscroll-contain py-1">
            {filteredCats.length === 0 ? (
              <p className="px-3 py-4 text-[12px] text-black/30 text-center">No categories found</p>
            ) : (
              filteredCats.map((cat) => {
                const isSelected = selected.includes(cat.slug);
                return (
                  <button
                    key={cat.slug}
                    onClick={() => toggleCat(cat.slug)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-black/[0.02] transition-colors"
                  >
                    <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-all ${
                      isSelected ? "bg-violet-800 border-violet-800" : "border-black/15"
                    }`}>
                      {isSelected && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    <span className={`text-[12px] flex-1 truncate ${isSelected ? "font-medium text-violet-800" : "text-black/60"}`}>
                      {cat.name}
                    </span>
                    <span className="text-xs text-black/25 flex-shrink-0">{cat.count}</span>
                  </button>
                );
              })
            )}
          </div>

          {selected.length > 0 && (
            <div className="p-2 border-t border-black/[0.04]">
              <button
                onClick={clearCats}
                className="w-full py-1.5 text-xs font-medium text-violet-800 hover:bg-black/[0.03] rounded-lg transition-colors"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
